import React, { useEffect, lazy, Suspense } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import pixelService from "./services/pixelService";
import { startPresence } from "./services/presenceService";
import UserAgentGuard from "./components/UserAgentGuard";
import useTheme from "./hooks/useTheme";
import useSiteInfo from "./hooks/useSiteInfo";
import useUtmify from "./hooks/useUtmify";
import { CartProvider } from "./context/CartContext";

// Lazy load all pages for faster initial load
const HomePage = lazy(() => import("./pages/HomePage"));
const ProductPage = lazy(() => import("./pages/ProductPage"));
const CheckoutPage = lazy(() => import("./pages/CheckoutPage"));
const AdminPage = lazy(() => import("./pages/AdminPage"));
const CartPage = lazy(() => import("./pages/CartPage"));
const PrivacyPolicyPage = lazy(() => import("./pages/PrivacyPolicyPage"));
const ReturnsPage = lazy(() => import("./pages/ReturnsPage"));
const TermsPage = lazy(() => import("./pages/TermsPage"));
const TrackingPage = lazy(() => import("./pages/TrackingPage"));
const PayPage = lazy(() => import("./pages/PayPage"));

// Minimal loading spinner
const PageLoader = () => (
  <div style={{
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100vh',
    background: '#fff'
  }}>
    <div style={{
      width: 32,
      height: 32,
      border: '3px solid #e5e7eb',
      borderTopColor: '#14453D',
      borderRadius: '50%',
      animation: 'spin 0.6s linear infinite'
    }} />
  </div>
);

function App() {
  useEffect(() => {
    // Delay pixel init to not block render
    const timer = setTimeout(() => {
      pixelService.init();
    }, 100);
    // Start real-time presence heartbeat (no IP storage; in-memory only)
    startPresence();
    return () => clearTimeout(timer);
  }, []);

  useTheme();
  useSiteInfo();
  useUtmify();

  return (
    <div className="App">
      <CartProvider>
        <BrowserRouter>
          <Suspense fallback={<PageLoader />}>
            <Routes>
              <Route path="/" element={<UserAgentGuard><HomePage /></UserAgentGuard>} />
              <Route path="/carrinho" element={<CartPage />} />
              <Route path="/checkout" element={<UserAgentGuard><CheckoutPage /></UserAgentGuard>} />
              <Route path="/checkout/:productId" element={<UserAgentGuard><CheckoutPage /></UserAgentGuard>} />
              <Route path="/produto/:productId" element={<UserAgentGuard><ProductPage /></UserAgentGuard>} />
              <Route path="/privacidade" element={<PrivacyPolicyPage />} />
              <Route path="/trocas-devolucoes" element={<ReturnsPage />} />
              <Route path="/termos-de-uso" element={<TermsPage />} />
              <Route path="/rastreio" element={<TrackingPage />} />
              <Route path="/rastreio/:code" element={<TrackingPage />} />
              <Route path="/pagar/:token" element={<PayPage />} />
              <Route path="/admin" element={<AdminPage />} />
            </Routes>
          </Suspense>
        </BrowserRouter>
      </CartProvider>
    </div>
  );
}

export default App;
